/**
 * The status filter over a letter list (David, Oct 15 2026: every status can be filtered for, in
 * the Org Planning Overview's checkbox dropdown). Every status that occurs, in the order a letter
 * moves through them, each with its count; counts and rows come from the same `letterStatus`, so
 * they cannot disagree. `null` = all statuses — kept apart from "every box ticked" so a status that
 * appears on a reload is shown too. Nothing ticked also shows everything, as in Org Planning
 * (`timelineFilters.ts`), and the button says "All". A ticked status no row still has stays
 * offered (at 0), so it can be unticked. Shared by the leaver list and the interim list.
 */
import { MultiSelect } from '../../components/ticketing/MultiSelect';
import { STATUS_ORDER, type LetterStatus, type StatusSelection } from './letterStatus';

/** Position in the lifecycle; unknown labels last. */
const rank = (label: string) => {
  const i = STATUS_ORDER.indexOf(label);
  return i === -1 ? STATUS_ORDER.length : i;
};

/** The dropdown for these statuses (one per row of the list, before filtering). */
export function StatusFilter({ statuses, selected, onChange }: { statuses: LetterStatus[]; selected: StatusSelection; onChange: (next: StatusSelection) => void }) {
  const counts = new Map<string, number>();
  for (const s of statuses) counts.set(s.label, (counts.get(s.label) ?? 0) + 1);
  for (const label of selected ?? []) if (!counts.has(label)) counts.set(label, 0);
  const options = [...counts.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([label, count]) => ({ value: label, label: `${label} (${count})` }));
  const all = new Set(options.map((o) => o.value));
  return (
    <MultiSelect
      label="Status"
      options={options}
      selected={selected ?? all}
      // Every box ticked is "all" again, so later statuses are not filtered out by an old choice.
      onChange={(next) => onChange(options.every((o) => next.has(o.value)) ? null : next)}
      align="left"
      triggerClassName="h-10 rounded"
    />
  );
}
